import { getBuiltInWidgetConfigByWidget, widgetMatchesSearch } from "components/dashboard/grid/helpers"
import React, { FC, useEffect, useState } from "react"
import { Button } from "../../core/button"
import { getModalMaxWidth, Modal } from "../../core/modal/modal"
import { CustomWidgetModal } from "components/dashboard/widgets/customWidgets/customWidgetModal"
import classNames from "classnames"
import { useUiContext } from "components/providers/uiProvider"
import { WidgetLayout } from "components/dashboard/widgets/shared/widgetLayout"
import { Group } from "domainModels/group"
import { Widget } from "domainModels/widget"
import { useCurrentDashboard, useIsDashboardOwner, useUpdateDashboard } from "reactQuery/dashboard"
import { useUpdateGroup } from "reactQuery/group"
import { TOAST_GROUP_UPDATE } from "components/dashboard/toastConfig"
import toast from "react-hot-toast"
import { InlineEdit } from "components/core/inlineEdit"

interface Props {
  group: Group
  groupWidgets: Widget[]
}

export const GroupWidget: FC<Props> = ({ group, groupWidgets }) => {
  const { data: dashboard } = useCurrentDashboard()
  const [showModal, setShowModal] = useState(false)
  const [selectedWidget, setSelectedWidget] = useState<Widget>()
  const [groupName, setGroupName] = useState(group.name)
  const { value } = useUiContext()
  const { searchQuery } = value
  const { mutateAsync: updateGroup } = useUpdateGroup()
  const { mutateAsync: updateDashboard } = useUpdateDashboard()
  const isDashboardOwner = useIsDashboardOwner()
  const isGroupBlurred = !groupWidgets.some((w) =>
    w.id && searchQuery
      ? widgetMatchesSearch({ name: w.information.name, keywords: w.information.keywords, searchQuery })
      : true
  )

  useEffect(() => {
    const widget = groupWidgets.find((w) => w.id === selectedWidget?.id)
    if (widget) {
      setSelectedWidget(widget)
    }
  }, [groupWidgets, selectedWidget?.id])

  const updateGroupName = async () => {
    if (groupName !== group.name && dashboard?.id) {
      await toast.promise(
        updateGroup({
          dashboardId: dashboard.id,
          group: {
            ...group,
            name: groupName,
          },
        }),
        TOAST_GROUP_UPDATE
      )
      updateDashboard(dashboard)
    }
  }

  return (
    <WidgetLayout
      layout={group.layout}
      isLocked={group.layout.xxl.static}
      isBlurred={isGroupBlurred}
      isDashboardOwner={isDashboardOwner}
    >
      <div className="flex items-center mb-3">
        <div className="relative z-10 font-medium truncate max-w-full mr-4 -mt-0.5">
          <InlineEdit
            id={groupName}
            disabled={!isDashboardOwner}
            value={groupName}
            onChange={(ev) => setGroupName(ev.target.value)}
            onBlur={updateGroupName}
            onKeyUp={(ev) => {
              if (ev.code === "Enter") {
                updateGroupName()
              }
            }}
          />
        </div>
      </div>
      <div className="overflow-auto z-10 rounded-md flex flex-wrap gap-2">
        {groupWidgets.map((widget) => {
          const builtInWidget = getBuiltInWidgetConfigByWidget(widget)
          const isBlurred =
            searchQuery &&
            searchQuery.length > 0 &&
            !widgetMatchesSearch({
              name: widget.information.name,
              keywords: widget.information.keywords,
              searchQuery,
            })

          return (
            <Button
              key={widget.id}
              onClick={() => {
                setSelectedWidget(widget)
                setShowModal(true)
              }}
              variant="secondary"
              className={classNames({
                "blur pointer-events-none": isBlurred,
                "relative z-10": !isBlurred,
              })}
              draggable={true}
              unselectable="on"
              onDragStart={(e) => {
                window.DRAGGING_GROUP_ITEM = widget.id?.toString()
                // this is a hack for firefox
                // Firefox requires some kind of initialization
                // which we can do by adding this attribute
                // @see https://bugzilla.mozilla.org/show_bug.cgi?id=568313
                e.dataTransfer.setData("text/plain", widget.id?.toString() ?? "")
              }}
              onDragEnd={() => {
                window.DRAGGING_GROUP_ITEM = undefined
              }}
            >
              {builtInWidget?.icon && (
                <span className="mr-1.5 -ml-1.5 w-5 h-5 text-indigo-800 dark:text-indigo-400">
                  {builtInWidget.icon}
                </span>
              )}
              {widget.information.name}
            </Button>
          )
        })}
      </div>
      <Modal
        show={showModal}
        className={getModalMaxWidth(selectedWidget)}
        onClose={() => {
          setShowModal(false)
        }}
        afterLeave={() => {
          setSelectedWidget(undefined)
        }}
      >
        {selectedWidget && <ModalContent widget={selectedWidget} />}
      </Modal>
    </WidgetLayout>
  )
}

const ModalContent: FC<{ widget: Widget }> = ({ widget }) => {
  const builtInWidget = getBuiltInWidgetConfigByWidget(widget)

  return builtInWidget?.renderModal ? (
    <>{builtInWidget.renderModal({ widget })}</>
  ) : (
    <CustomWidgetModal widget={widget} />
  )
}
